import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import CommandPalette from '../CommandPalette/CommandPalette';
import { useLanguage } from '../../contexts/LanguageContext';
import { darkModeClass } from '../../utils/cn';
import styles from './Header.module.css';

const navItems = [
  { id: 'projects', key: 'projects' },
  { id: 'skills', key: 'skills' },
  { id: 'experience', key: 'experience' },
  { id: 'contact', key: 'contact' }
];

const Header = () => {
  const { isDarkMode } = useTheme();
  const { language, toggleLanguage, t, data } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const sections = navItems
      .map(item => document.getElementById(item.id))
      .filter(Boolean);

    const observer = new IntersectionObserver(entries => {
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActiveSection(visible.target.id);
    }, { rootMargin: '-25% 0px -60% 0px', threshold: [0, 0.1] });

    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const scrollY = isMenuOpen ? window.scrollY : 0;
    if (isMenuOpen) {
      document.body.style.setProperty('overflow', 'hidden');
      document.body.style.setProperty('position', 'fixed');
      document.body.style.setProperty('top', `-${scrollY}px`);
    }
    return () => {
      const top = document.body.style.top;
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      if (top) window.scrollTo(0, parseFloat(top) * -1);
    };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={`${styles.header} ${darkModeClass(styles, isDarkMode)}`}>
      <div className={styles.container}>
        <a href="#top" className={styles.logo} onClick={closeMenu} aria-label={t('nav.home')}>
          <span className={styles.logoText}>{data.personal.name.split(' ')[0]}</span>
          <span className={styles.logoDot}>.</span>
        </a>

        <nav className={`${styles.nav} ${isMenuOpen ? styles.navOpen : ''}`} aria-label={t('nav.main')}>
          <ul className={styles.navList}>
            {navItems.map(item => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`${styles.navLink} ${activeSection === item.id ? styles.active : ''}`}
                  onClick={closeMenu}
                >
                  {t(`nav.${item.key}`)}
                </a>
              </li>
            ))}
          </ul>
          <p className={styles.mobileHint}>{t('nav.menuHint')}</p>
        </nav>

        {isMenuOpen && <button className={styles.menuBackdrop} onClick={closeMenu} aria-label={t('nav.closeMenu')} />}

        <div className={styles.quickNavContainer}>
          <CommandPalette />
        </div>

        <button className={styles.languageToggle} onClick={toggleLanguage} aria-label={t('language.switchTo')} title={t('language.switchTo')}>
          <i className="fas fa-globe"></i>
          <span>{language === 'en' ? 'AR' : 'EN'}</span>
        </button>

        <div className={styles.themeToggleContainer}>
          <ThemeToggle />
        </div>

        <button
          className={styles.menuButton}
          onClick={() => setIsMenuOpen(open => !open)}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
        >
          <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
      </div>
    </header>
  );
};

export default Header;
